/**
 * Video Slider frontend behavior: horizontal scroll-snap nav + a video modal.
 */
class VideoSlider {
	constructor( node ) {
		this.node = node;
		this.track = node.querySelector( '.video-slider__track' );
		this.slides = this.track ? Array.from( this.track.children ) : [];
		this.prevBtn = node.querySelector( '[data-video-slider-prev]' );
		this.nextBtn = node.querySelector( '[data-video-slider-next]' );
		this.dots = Array.from( node.querySelectorAll( '[data-video-slider-dot]' ) );
		this.modal = node.querySelector( '[data-video-slider-modal]' );
		this.modalTitle = node.querySelector( '[data-video-slider-modal-title]' );
		this.modalMedia = node.querySelector( '[data-video-slider-modal-media]' );
		this.lastTrigger = null;

		if ( ! this.track || ! this.slides.length ) {
			return;
		}

		// Guards against the browser's scroll-anchoring nudging the track away
		// from the start as async video/image loads shift layout post-paint.
		this.track.scrollLeft = 0;

		this.bindNav();
		this.bindDots();
		this.bindTriggers();
		this.bindModal();

		this.track.addEventListener( 'scroll', debounce( () => this.syncNavState(), 100 ) );
		window.addEventListener( 'resize', debounce( () => this.syncNavState(), 150 ) );

		this.syncNavState();
	}

	bindNav() {
		if ( this.prevBtn ) {
			this.prevBtn.addEventListener( 'click', () => this.goToIndex( this.getIndex() - 1 ) );
		}
		if ( this.nextBtn ) {
			this.nextBtn.addEventListener( 'click', () => this.goToIndex( this.getIndex() + 1 ) );
		}
	}

	bindDots() {
		this.dots.forEach( ( dot, index ) => {
			dot.addEventListener( 'click', () => this.goToIndex( index ) );
		} );
	}

	getPaddingOffset() {
		return Math.max( parseInt( getComputedStyle( this.track ).paddingLeft, 10 ) || 0, 0 );
	}

	getIndex() {
		const offset = this.getPaddingOffset();
		const distances = this.slides.map( ( slide ) => Math.abs( slide.offsetLeft - this.track.scrollLeft - offset ) );
		return distances.indexOf( Math.min( ...distances ) );
	}

	goToIndex( index ) {
		const clampedIndex = Math.max( 0, Math.min( index, this.slides.length - 1 ) );
		const target = this.slides[ clampedIndex ];

		if ( ! target ) {
			return;
		}

		this.track.scrollTo( {
			left: Math.max( 0, target.offsetLeft - this.getPaddingOffset() ),
			behavior: 'smooth',
		} );
	}

	syncNavState() {
		const index = this.getIndex();

		if ( this.prevBtn ) {
			this.prevBtn.disabled = index <= 0;
		}
		if ( this.nextBtn ) {
			this.nextBtn.disabled = index >= this.slides.length - 1;
		}

		this.dots.forEach( ( dot, dotIndex ) => {
			dot.classList.toggle( 'is-active', dotIndex === index );
		} );
	}

	bindTriggers() {
		this.node.querySelectorAll( '[data-video-slider-trigger]' ).forEach( ( trigger ) => {
			trigger.addEventListener( 'click', () => this.openModal( trigger ) );
		} );
	}

	bindModal() {
		if ( ! this.modal ) {
			return;
		}

		this.modal.querySelectorAll( '[data-video-slider-modal-close]' ).forEach( ( closeEl ) => {
			closeEl.addEventListener( 'click', () => this.closeModal() );
		} );

		document.addEventListener( 'keydown', ( event ) => {
			if ( 'Escape' === event.key && ! this.modal.hasAttribute( 'hidden' ) ) {
				this.closeModal();
			}
		} );
	}

	openModal( trigger ) {
		const src = trigger.getAttribute( 'data-video-slider-src' ) || '';

		if ( ! this.modal || ! this.modalMedia || ! src ) {
			return;
		}

		this.lastTrigger = trigger;

		if ( this.modalTitle ) {
			this.modalTitle.textContent = trigger.getAttribute( 'data-video-slider-title' ) || '';
		}

		this.modalMedia.replaceChildren();

		const video = document.createElement( 'video' );
		video.controls = true;
		video.autoplay = true;
		video.playsInline = true;

		const source = document.createElement( 'source' );
		source.src = src;
		source.type = trigger.getAttribute( 'data-video-slider-type' ) || 'video/mp4';
		video.appendChild( source );

		this.modalMedia.appendChild( video );

		this.modal.removeAttribute( 'hidden' );
		document.body.classList.add( 'video-slider-modal-open' );
	}

	closeModal() {
		if ( ! this.modal ) {
			return;
		}

		const video = this.modalMedia ? this.modalMedia.querySelector( 'video' ) : null;
		if ( video ) {
			video.pause();
		}
		if ( this.modalMedia ) {
			this.modalMedia.replaceChildren();
		}

		this.modal.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'video-slider-modal-open' );

		if ( this.lastTrigger ) {
			this.lastTrigger.focus();
			this.lastTrigger = null;
		}
	}
}

function debounce( fn, wait ) {
	let timeoutId;
	return ( ...args ) => {
		window.clearTimeout( timeoutId );
		timeoutId = window.setTimeout( () => fn( ...args ), wait );
	};
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.video-slider' ).forEach( ( node ) => {
		new VideoSlider( node );
	} );
} );
